import { ChangeDetectionStrategy, Component } from '@angular/core';

import { ModalBody, ModalFooter, ModalHeader, ModalService, injectModalData, injectModalRef } from '../modal';

/** The part of each page's CalState the sheet reads and writes. */
export interface DateSheetCal {
  year: number;
  month: number;
  selected: Date | null;
}

export interface DateSheetData {
  title: string;
  selected: Date | null;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Phone version of the playground's date dropdown (.cal-dropdown): the same
 * month grid in a bottom sheet with bigger day targets. Picking a day closes
 * with that date, Clear closes with null, ✕/backdrop leave the field as it was.
 */
@Component({
  selector: 'app-date-sheet',
  standalone: true,
  imports: [ModalHeader, ModalBody, ModalFooter],
  templateUrl: './date-sheet.html',
  styleUrl: './date-sheet.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateSheet {
  readonly data = injectModalData<DateSheetData>();
  readonly modalRef = injectModalRef<Date | null>();

  readonly MONTH_NAMES = MONTH_NAMES;
  readonly DAY_NAMES = DAY_NAMES;

  year = (this.data.selected ?? new Date()).getFullYear();
  month = (this.data.selected ?? new Date()).getMonth();
  days = this.buildDays();

  prevMonth(): void {
    if (this.month === 0) { this.month = 11; this.year--; } else this.month--;
    this.days = this.buildDays();
  }

  nextMonth(): void {
    if (this.month === 11) { this.month = 0; this.year++; } else this.month++;
    this.days = this.buildDays();
  }

  pick(day: number | null): void {
    if (day) this.modalRef.close(new Date(this.year, this.month, day));
  }

  isSelected(day: number | null): boolean {
    const s = this.data.selected;
    return !!(day && s && s.getDate() === day && s.getMonth() === this.month && s.getFullYear() === this.year);
  }

  isToday(day: number | null): boolean {
    const t = new Date();
    return !!day && t.getDate() === day && t.getMonth() === this.month && t.getFullYear() === this.year;
  }

  private buildDays(): (number | null)[] {
    const first = new Date(this.year, this.month, 1).getDay();
    const count = new Date(this.year, this.month + 1, 0).getDate();
    const cells: (number | null)[] = Array(first).fill(null);
    for (let d = 1; d <= count; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    return cells;
  }
}

/** Same sheet for a field that keeps its date as a 'YYYY-MM-DD' string (the
 *  superadmin filters, mirrored in the URL): onPick gets the new string,
 *  '' after Clear, and isn't called when the sheet is dismissed. */
export function openIsoDateSheet(
  modalService: ModalService,
  title: string,
  value: string,
  onPick: (value: string) => void,
): void {
  modalService.medium(DateSheet, { data: { title, selected: isoToDate(value) } }).closed$.subscribe(result => {
    if (result !== undefined) onPick(result ? dateToIso(result) : '');
  });
}

/** 'YYYY-MM-DD' as the sheet and the filters show it, e.g. "26 Oct 2026". */
export function formatIsoDate(value: string): string {
  const date = isoToDate(value);
  return date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

// Local dates both ways: toISOString() would shift the day east of UTC.
function isoToDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

function dateToIso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Opens the sheet for one date field and writes the pick back to its
 *  CalState, the same fields selectDay()/clearDate() set on desktop. */
export function openDateSheet(modalService: ModalService, title: string, cal: DateSheetCal): void {
  modalService.medium(DateSheet, { data: { title, selected: cal.selected } }).closed$.subscribe(result => {
    if (result === undefined) return;
    cal.selected = result;
    if (result) {
      cal.year = result.getFullYear();
      cal.month = result.getMonth();
    }
  });
}
